import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, Lock, X, Plus, CheckCircle, TrendingUp, Euro, ToggleLeft, ToggleRight, Table, BarChart3, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENT_YEAR = new Date().getFullYear();

const FR_MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin",
  "Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function fillRate(rate) {
  if (rate >= 75) return { color: "text-green-700", bg: "bg-green-500", badge: "bg-green-100 text-green-700" };
  if (rate >= 40) return { color: "text-amber-700", bg: "bg-amber-400", badge: "bg-amber-100 text-amber-700" };
  return { color: "text-gray-500", bg: "bg-gray-300", badge: "bg-gray-100 text-gray-600" };
}

// ── Block Modal ───────────────────────────────────────────────────────────────
function BlockModal({ date, vehicles, existingBlock, onClose, onSaved }) {
  const { authHeaders } = useAuth();
  const [form, setForm] = useState({
    date: date,
    vehicle_id: existingBlock?.vehicle_id || "",
    reason: existingBlock?.reason || "Congés",
    notes: existingBlock?.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const REASONS = ["Congés","Maintenance","Événement personnel","Réservé","Autre"];

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/calendar/blocks`, { ...form, vehicle_id: form.vehicle_id || null }, { headers: authHeaders });
      onSaved();
    } finally { setSaving(false); }
  };

  const deleteBlock = async () => {
    await axios.delete(`${API}/calendar/blocks/${existingBlock.id}`, { headers: authHeaders });
    onSaved();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-sm w-full shadow-2xl rounded p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-gray-900">{existingBlock ? "Modifier le blocage" : "Bloquer ce créneau"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {new Date(date + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">Véhicule concerné</label>
            <select value={form.vehicle_id} onChange={e => setForm(f => ({ ...f, vehicle_id: e.target.value }))}
              className="w-full border border-gray-200 px-3 py-2.5 text-sm rounded focus:outline-none focus:border-blue-400">
              <option value="">Tous les véhicules</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">Motif</label>
            <select value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              className="w-full border border-gray-200 px-3 py-2.5 text-sm rounded focus:outline-none focus:border-blue-400">
              {REASONS.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wider">Notes</label>
            <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="Détails optionnels..."
              className="w-full border border-gray-200 px-3 py-2.5 text-sm rounded focus:outline-none focus:border-blue-400" />
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          {existingBlock && (
            <button onClick={deleteBlock} className="px-4 py-2 border border-red-200 text-red-500 text-sm font-semibold rounded hover:bg-red-50">
              Supprimer
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-sm font-semibold rounded">Annuler</button>
          <button onClick={save} disabled={saving}
            className="px-4 py-2 bg-[#1B2E4B] text-white text-sm font-semibold rounded hover:bg-[#162440] disabled:opacity-60">
            {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Annual View ───────────────────────────────────────────────────────────────
function AnnualView({ year, data, onSelectMonth }) {
  if (!data) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  const kpis = data.annual_kpis;
  const fr = fillRate(kpis.fill_rate);

  return (
    <div className="space-y-6">
      {/* Annual KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Taux de remplissage", value: `${kpis.fill_rate}%`, sub: `${kpis.booked_slots}/${kpis.total_slots} créneaux`, color: fr.color, badge: fr.badge },
          { label: "CA annuel", value: `${kpis.ca.toLocaleString("fr-FR")}€`, sub: "Réservations confirmées", color: "text-[#D4A843]", badge: "bg-amber-100 text-amber-700" },
          { label: "Créneaux réservés", value: kpis.booked_slots, sub: `sur ${kpis.total_slots} disponibles`, color: "text-[#1B2E4B]", badge: "bg-blue-100 text-blue-700" },
          { label: "Véhicules suivis", value: data.tracked_vehicles.length, sub: data.tracked_vehicles.map(v => v.name).join(", ") || "Aucun", color: "text-purple-700", badge: "bg-purple-100 text-purple-700" },
        ].map(({ label, value, sub, color, badge }) => (
          <div key={label} className="bg-white p-5 shadow-sm rounded">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            <p className="text-xs text-gray-400 mt-1 truncate">{sub}</p>
          </div>
        ))}
      </div>

      {/* Monthly grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {data.months.map(m => {
          const fr2 = fillRate(m.fill_rate);
          return (
            <button key={m.month} onClick={() => onSelectMonth(m.month)}
              data-testid={`month-card-${m.month}`}
              className="bg-white p-4 shadow-sm rounded text-left hover:shadow-md hover:border-blue-200 border-2 border-transparent transition-all group">
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-gray-900 text-sm group-hover:text-[#1B2E4B]">{m.month_name}</span>
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${fr2.badge}`}>{m.fill_rate}%</span>
              </div>

              {/* Weekend mini dots */}
              <div className="flex flex-wrap gap-1 mb-3">
                {m.weekends.map((wk, i) => (
                  <div key={i} className="flex gap-0.5">
                    <div className={`w-2.5 h-2.5 rounded-sm ${
                      wk.fri_blocked ? "bg-red-300" :
                      wk.fri_bookings > 0 ? "bg-[#D4A843]" : "bg-gray-200"
                    }`} title={`Vendredi ${wk.friday}`} />
                    {wk.saturday && (
                      <div className={`w-2.5 h-2.5 rounded-sm ${
                        wk.sat_blocked ? "bg-red-300" :
                        wk.sat_bookings > 0 ? "bg-[#D4A843]" : "bg-gray-200"
                      }`} title={`Samedi ${wk.saturday}`} />
                    )}
                  </div>
                ))}
              </div>

              {/* Progress bar */}
              <div className="h-1.5 bg-gray-100 rounded-full mb-2">
                <div className={`h-1.5 rounded-full ${fr2.bg}`} style={{ width: `${m.fill_rate}%` }} />
              </div>

              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">{m.booked_slots}/{m.total_slots} créneaux</span>
                <span className="text-xs font-bold text-[#1B2E4B]">{m.ca > 0 ? `${m.ca.toLocaleString("fr-FR")}€` : "-"}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-gray-500 justify-center">
        {[["bg-[#D4A843]","Réservé"],["bg-gray-200","Libre"],["bg-red-300","Bloqué"]].map(([bg, label]) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm ${bg}`} /> {label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Monthly View ──────────────────────────────────────────────────────────────
function MonthlyView({ year, month, onPrev, onNext, onYearChange }) {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockModal, setBlockModal] = useState(null); // { date, existingBlock? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/month?year=${year}&month=${month}`, { headers: authHeaders });
      setData(r.data);
    } finally { setLoading(false); }
  }, [year, month, authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!data) return null;

  const kpis = data.kpis;
  const fr = fillRate(kpis.fill_rate);

  return (
    <div className="space-y-5">
      {/* Navigation */}
      <div className="flex items-center gap-4">
        <button onClick={onPrev} className="p-2 hover:bg-gray-100 rounded transition-colors"><ChevronLeft size={20} /></button>
        <h2 className="text-xl font-bold text-gray-900 min-w-[180px] text-center">
          {data.month_name} {year}
        </h2>
        <button onClick={onNext} className="p-2 hover:bg-gray-100 rounded transition-colors"><ChevronRight size={20} /></button>
      </div>

      {/* Month KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Taux de remplissage", value: `${kpis.fill_rate}%`, color: fr.color },
          { label: "CA du mois", value: `${kpis.ca.toLocaleString("fr-FR")}€`, color: "text-[#D4A843]" },
          { label: "Créneaux réservés", value: `${kpis.booked_slots}/${kpis.total_slots}`, color: "text-[#1B2E4B]" },
          { label: "Week-ends", value: `${kpis.weekends_count} WE`, color: "text-gray-700" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white p-4 shadow-sm rounded">
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-xl font-bold ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Progress */}
      <div className="bg-white p-4 shadow-sm rounded">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-semibold text-gray-700">Taux de remplissage</span>
          <span className={`font-bold ${fr.color}`}>{kpis.fill_rate}%</span>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-3 rounded-full transition-all ${fr.bg}`} style={{ width: `${kpis.fill_rate}%` }} />
        </div>
      </div>

      {/* Weekends table */}
      <div className="bg-white shadow-sm rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#1B2E4B] text-white">
                <th className="px-4 py-3 text-left font-semibold">Jour</th>
                {data.tracked_vehicles.map(v => (
                  <th key={v.id} className="px-4 py-3 text-left font-semibold">{v.name}</th>
                ))}
                <th className="px-4 py-3 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.weekends.map((wk, wi) => (
                wk.days.map((day, di) => (
                  <tr key={day.date}
                    className={`border-b border-gray-50 ${di === 0 ? "border-t-2 border-gray-200" : ""} ${wi % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{day.day_name}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(day.date + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                      </p>
                    </td>
                    {day.slots.map(slot => (
                      <td key={slot.vehicle_id} className="px-4 py-3">
                        {slot.status === "réservé" && slot.devis ? (
                          <div className="flex items-start gap-2">
                            <span className="w-2 h-2 rounded-full bg-[#D4A843] mt-1.5 flex-shrink-0" />
                            <div>
                              <p className="font-semibold text-gray-900 text-xs leading-tight">
                                {slot.devis.client_name}
                              </p>
                              <p className="text-xs text-gray-400">{slot.devis.montant}€ · {slot.devis.numero}</p>
                            </div>
                          </div>
                        ) : slot.status === "bloqué" ? (
                          <div className="flex items-center gap-1.5 text-red-500">
                            <Lock size={12} />
                            <span className="text-xs font-semibold">{slot.block?.reason || "Bloqué"}</span>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1.5 text-gray-300 text-xs">
                            <CheckCircle size={12} className="text-green-300" /> Libre
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setBlockModal({ date: day.date, existingBlock: day.slots.find(s => s.block)?.block || null })}
                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-[#1B2E4B] transition-colors font-semibold border border-gray-200 px-2 py-1 rounded hover:border-gray-400"
                        title="Bloquer / Débloquer">
                        <Lock size={10} /> Bloquer
                      </button>
                    </td>
                  </tr>
                ))
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Block modal */}
      {blockModal && (
        <BlockModal
          date={blockModal.date}
          vehicles={data.tracked_vehicles}
          existingBlock={blockModal.existingBlock}
          onClose={() => setBlockModal(null)}
          onSaved={() => { setBlockModal(null); load(); }}
        />
      )}
    </div>
  );
}

// ── Prévisionnel View ─────────────────────────────────────────────────────────
function PrevisionnelView({ year }) {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ devis: true, reserve: true, annule: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year,
        include_devis: filters.devis,
        include_reserve: filters.reserve,
        include_annule: filters.annule,
      });
      const r = await axios.get(`${API}/previsionnel/summary?${params}`, { headers: authHeaders });
      setData(r.data);
    } finally { setLoading(false); }
  }, [year, filters, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggleFilter = (key) => {
    setFilters(f => ({ ...f, [key]: !f[key] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const gt = data?.grand_total || { total: 0, facture: 0, reste: 0, count: 0 };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 shadow-sm rounded">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-sm font-semibold text-gray-700">Afficher :</span>
          {[
            { key: "devis", label: "Devis / En attente", color: "orange" },
            { key: "reserve", label: "Réservations", color: "green" },
            { key: "annule", label: "Annulés", color: "red" },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => toggleFilter(key)}
              data-testid={`toggle-${key}`}
              className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all ${
                filters[key]
                  ? `bg-${color}-100 text-${color}-700 border border-${color}-200`
                  : "bg-gray-100 text-gray-400 border border-gray-200"
              }`}
            >
              {filters[key] ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 shadow-sm rounded">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Total {year}</p>
          <p className="text-2xl font-bold text-gray-900">{gt.total.toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-gray-400 mt-1">{gt.count} prestation(s)</p>
        </div>
        <div className="bg-white p-5 shadow-sm rounded">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Facturé</p>
          <p className="text-2xl font-bold text-blue-600">{gt.facture.toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-gray-400 mt-1">{gt.total > 0 ? Math.round((gt.facture / gt.total) * 100) : 0}% du total</p>
        </div>
        <div className="bg-white p-5 shadow-sm rounded">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Restant à facturer</p>
          <p className={`text-2xl font-bold ${gt.reste > 0 ? "text-orange-500" : "text-green-600"}`}>
            {gt.reste.toLocaleString("fr-FR")}€
          </p>
          <p className="text-xs text-gray-400 mt-1">{gt.total > 0 ? Math.round((gt.reste / gt.total) * 100) : 0}% du total</p>
        </div>
        <div className="bg-white p-5 shadow-sm rounded">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Taux de recouvrement</p>
          <p className={`text-2xl font-bold ${gt.total > 0 && gt.reste === 0 ? "text-green-600" : "text-[#1B2E4B]"}`}>
            {gt.total > 0 ? Math.round((gt.facture / gt.total) * 100) : 0}%
          </p>
          <div className="h-1.5 bg-gray-100 rounded-full mt-2">
            <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${gt.total > 0 ? (gt.facture / gt.total) * 100 : 0}%` }} />
          </div>
        </div>
      </div>

      {/* Monthly Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {data?.months?.map(m => {
          const hasData = m.count > 0;
          const recov = m.total > 0 ? Math.round((m.facture / m.total) * 100) : 0;
          return (
            <div
              key={m.month}
              data-testid={`prev-month-${m.month}`}
              className={`bg-white p-4 shadow-sm rounded transition-all ${hasData ? "hover:shadow-md" : "opacity-60"}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="font-bold text-gray-900 text-sm">{m.name}</span>
                {hasData && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                    {m.count} presta.
                  </span>
                )}
              </div>

              {hasData ? (
                <>
                  {/* Status breakdown */}
                  <div className="flex gap-1 mb-3">
                    {m.count_devis > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-600 font-medium">
                        {m.count_devis} devis
                      </span>
                    )}
                    {m.count_reserve > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-600 font-medium">
                        {m.count_reserve} réservé
                      </span>
                    )}
                    {m.count_annule > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-600 font-medium">
                        {m.count_annule} annulé
                      </span>
                    )}
                  </div>

                  {/* Financial summary */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Total</span>
                      <span className="text-sm font-bold text-gray-900">{m.total.toLocaleString("fr-FR")}€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Facturé</span>
                      <span className="text-sm font-semibold text-blue-600">{m.facture.toLocaleString("fr-FR")}€</span>
                    </div>
                    {m.reste > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">Reste</span>
                        <span className="text-sm font-semibold text-orange-500">{m.reste.toLocaleString("fr-FR")}€</span>
                      </div>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="mt-3">
                    <div className="h-1.5 bg-gray-100 rounded-full">
                      <div
                        className={`h-1.5 rounded-full ${recov === 100 ? "bg-green-500" : "bg-blue-500"}`}
                        style={{ width: `${recov}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1 text-right">{recov}% recouvré</p>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-300 text-center py-4">Aucune prestation</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex gap-6 text-xs text-gray-500 justify-center">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-gray-900 inline-block" />Total</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-blue-500 inline-block" />Facturé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-orange-400 inline-block" />Reste</span>
      </div>
    </div>
  );
}

// ── Tableau Prévisionnel View ─────────────────────────────────────────────────
function TableauPrevisionnelView({ year }) {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ devis: true, reserve: true, annule: false });
  const [sortBy, setSortBy] = useState("month");
  const [sortDir, setSortDir] = useState("asc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year,
        include_devis: filters.devis,
        include_reserve: filters.reserve,
        include_annule: filters.annule,
      });
      const r = await axios.get(`${API}/previsionnel/table?${params}`, { headers: authHeaders });
      setData(r.data);
    } finally { setLoading(false); }
  }, [year, filters, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const toggleFilter = (key) => {
    setFilters(f => ({ ...f, [key]: !f[key] }));
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return <ArrowUpDown size={10} className="opacity-30" />;
    return sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
  };

  const sortedMonths = data?.months ? [...data.months].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortDir === "asc" ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal));
  }) : [];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const gt = data?.grand_total || {};
  
  // Prepare chart data
  const chartData = data?.months?.map(m => ({
    name: m.name.substring(0, 3),
    Devis: m.devis_amount,
    Réservations: m.reserve_amount,
    Facturé: m.facture_amount,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 shadow-sm rounded flex flex-wrap items-center gap-4">
        <span className="text-sm font-semibold text-gray-700">Filtres :</span>
        {[
          { key: "devis", label: "Devis / En attente", color: "amber" },
          { key: "reserve", label: "Réservations", color: "green" },
          { key: "annule", label: "Annulés", color: "red" },
        ].map(({ key, label, color }) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer" data-testid={`filter-${key}`}>
            <input
              type="checkbox"
              checked={filters[key]}
              onChange={() => toggleFilter(key)}
              className={`w-4 h-4 rounded accent-${color}-500`}
            />
            <span className={`text-sm ${filters[key] ? `text-${color}-700 font-medium` : "text-gray-400"}`}>
              {label}
            </span>
          </label>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white p-6 shadow-sm rounded">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 size={18} className="text-[#D4A843]" /> Évolution mensuelle {year}
        </h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v / 1000}k€`} />
            <Tooltip 
              formatter={(value) => [`${value.toLocaleString("fr-FR")}€`, ""]}
              contentStyle={{ fontSize: 12 }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Devis" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Réservations" fill="#22c55e" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Facturé" fill="#3b82f6" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="previsionnel-table">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold tracking-wider uppercase bg-gray-50">
                <th className="px-3 py-3 text-left cursor-pointer hover:text-gray-600" onClick={() => handleSort("month")}>
                  <span className="flex items-center gap-1">Mois <SortIcon column="month" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("reserve_amount")}>
                  <span className="flex items-center justify-end gap-1">CA Résa <SortIcon column="reserve_amount" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("devis_amount")}>
                  <span className="flex items-center justify-end gap-1">CA Devis <SortIcon column="devis_amount" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("costs_amount")}>
                  <span className="flex items-center justify-end gap-1">Coûts <SortIcon column="costs_amount" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("margin_amount")}>
                  <span className="flex items-center justify-end gap-1">Marge <SortIcon column="margin_amount" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("margin_rate")}>
                  <span className="flex items-center justify-end gap-1">% <SortIcon column="margin_rate" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("facture_amount")}>
                  <span className="flex items-center justify-end gap-1">Facturé <SortIcon column="facture_amount" /></span>
                </th>
                <th className="px-3 py-3 text-right cursor-pointer hover:text-gray-600" onClick={() => handleSort("reste_facture")}>
                  <span className="flex items-center justify-end gap-1">Reste <SortIcon column="reste_facture" /></span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedMonths.map((m, idx) => (
                <tr key={m.month} className={`border-b border-gray-50 hover:bg-gray-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                  <td className="px-3 py-3 font-semibold text-gray-900">{m.name}</td>
                  <td className="px-3 py-3 text-right font-medium text-green-600">
                    {m.reserve_amount > 0 ? `${m.reserve_amount.toLocaleString("fr-FR")}€` : "—"}
                    {m.reserve_count > 0 && <span className="text-[10px] text-gray-400 block">{m.reserve_count} résa</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-amber-600">
                    {m.devis_amount > 0 ? `${m.devis_amount.toLocaleString("fr-FR")}€` : "—"}
                    {m.devis_count > 0 && <span className="text-[10px] text-gray-400 block">{m.devis_count} devis</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-red-500">
                    {(m.costs_amount || 0) > 0 ? `${m.costs_amount.toLocaleString("fr-FR")}€` : "—"}
                  </td>
                  <td className="px-3 py-3 text-right font-semibold">
                    <span className={(m.margin_amount || 0) >= 0 ? "text-green-600" : "text-red-600"}>
                      {(m.margin_amount || 0) !== 0 ? `${m.margin_amount.toLocaleString("fr-FR")}€` : "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right">
                    {(m.margin_rate || 0) > 0 ? (
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${m.margin_rate >= 50 ? "bg-green-100 text-green-700" : m.margin_rate >= 30 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-600"}`}>
                        {m.margin_rate}%
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right font-medium text-blue-600">{m.facture_amount > 0 ? `${m.facture_amount.toLocaleString("fr-FR")}€` : "—"}</td>
                  <td className="px-3 py-3 text-right font-medium text-orange-500">{m.reste_facture > 0 ? `${m.reste_facture.toLocaleString("fr-FR")}€` : <span className="text-gray-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-[#1B2E4B] text-white font-semibold text-sm">
                <td className="px-3 py-3">TOTAL {year}</td>
                <td className="px-3 py-3 text-right">{(gt.reserve_amount || 0).toLocaleString("fr-FR")}€</td>
                <td className="px-3 py-3 text-right">{(gt.devis_amount || 0).toLocaleString("fr-FR")}€</td>
                <td className="px-3 py-3 text-right">
                  {(gt.costs_amount || 0) > 0 ? `${(gt.costs_amount || 0).toLocaleString("fr-FR")}€` : "—"}
                </td>
                <td className="px-3 py-3 text-right">
                  <span className={(gt.margin_amount || 0) >= 0 ? "text-green-300" : "text-red-300"}>
                    {(gt.margin_amount || 0) !== 0 ? `${(gt.margin_amount || 0).toLocaleString("fr-FR")}€` : "—"}
                  </span>
                </td>
                <td className="px-3 py-3 text-right">
                  {(gt.margin_rate || 0) > 0 ? (
                    <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs font-semibold">{gt.margin_rate}%</span>
                  ) : "—"}
                </td>
                <td className="px-3 py-3 text-right">{(gt.facture_amount || 0).toLocaleString("fr-FR")}€</td>
                <td className="px-3 py-3 text-right">
                  {(gt.reste_facture || 0) > 0 ? `${(gt.reste_facture || 0).toLocaleString("fr-FR")}€` : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <div className="bg-amber-50 border border-amber-200 p-4 rounded">
          <p className="text-xs text-amber-600 uppercase tracking-wider mb-1">Devis en cours</p>
          <p className="text-xl font-bold text-amber-700">{(gt.devis_amount || 0).toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-amber-500 mt-1">{gt.devis_count || 0} devis</p>
        </div>
        <div className="bg-green-50 border border-green-200 p-4 rounded">
          <p className="text-xs text-green-600 uppercase tracking-wider mb-1">Réservations</p>
          <p className="text-xl font-bold text-green-700">{(gt.reserve_amount || 0).toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-green-500 mt-1">{gt.reserve_count || 0} réservations</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 p-4 rounded">
          <p className="text-xs text-blue-600 uppercase tracking-wider mb-1">Facturé</p>
          <p className="text-xl font-bold text-blue-700">{(gt.facture_amount || 0).toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-blue-500 mt-1">{gt.reserve_amount > 0 ? Math.round((gt.facture_amount / gt.reserve_amount) * 100) : 0}% des réservations</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 p-4 rounded">
          <p className="text-xs text-orange-600 uppercase tracking-wider mb-1">Reste à facturer</p>
          <p className="text-xl font-bold text-orange-700">{(gt.reste_facture || 0).toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-orange-500 mt-1">Sur les réservations</p>
        </div>
        <div className="bg-red-50 border border-red-200 p-4 rounded">
          <p className="text-xs text-red-600 uppercase tracking-wider mb-1">Coûts totaux</p>
          <p className="text-xl font-bold text-red-700">{(gt.costs_amount || 0).toLocaleString("fr-FR")}€</p>
          <p className="text-xs text-red-400 mt-1">Charges véhicules</p>
        </div>
        <div className={`p-4 rounded border ${(gt.margin_amount || 0) >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
          <p className={`text-xs uppercase tracking-wider mb-1 ${(gt.margin_amount || 0) >= 0 ? "text-emerald-600" : "text-red-600"}`}>Marge globale</p>
          <p className={`text-xl font-bold ${(gt.margin_amount || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>
            {(gt.margin_amount || 0) !== 0 ? `${(gt.margin_amount || 0).toLocaleString("fr-FR")}€` : "—"}
          </p>
          <p className={`text-xs mt-1 ${(gt.margin_amount || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
            {(gt.margin_rate || 0) > 0 ? `Taux : ${gt.margin_rate}%` : "Aucun coût saisi"}
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AdminCalendrierMariages() {
  const { authHeaders } = useAuth();
  const [tab, setTab] = useState("annual");
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [annualData, setAnnualData] = useState(null);
  const [loadingAnnual, setLoadingAnnual] = useState(true);

  const loadAnnual = useCallback(async () => {
    setLoadingAnnual(true);
    try {
      const r = await axios.get(`${API}/calendar/year?year=${year}`, { headers: authHeaders });
      setAnnualData(r.data);
    } finally { setLoadingAnnual(false); }
  }, [year, authHeaders]);

  useEffect(() => { loadAnnual(); }, [loadAnnual]);

  const selectMonth = (m) => { setMonth(m); setTab("monthly"); };

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="calendar-title">
            Calendrier & Prévisionnel
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {tab === "previsionnel" 
              ? "Récapitulatif mensuel des encaissements prévisionnels"
              : "Suivi des créneaux vendredi & samedi · Taux de remplissage · CA mensuel"
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(y => y - 1)} className="p-2 hover:bg-gray-100 rounded">
            <ChevronLeft size={18} />
          </button>
          <span className="font-bold text-gray-900 text-lg w-14 text-center">{year}</span>
          <button onClick={() => setYear(y => y + 1)} className="p-2 hover:bg-gray-100 rounded">
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        {[
          { key: "annual", label: "Vue annuelle" },
          { key: "monthly", label: `Vue mensuelle — ${FR_MONTHS[month - 1]} ${year}` },
          { key: "previsionnel", label: "Prévisionnel" },
          { key: "tableau", label: "Tableau & Graphique" },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.key ? "border-[#1B2E4B] text-[#1B2E4B]" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "annual" ? (
        loadingAnnual
          ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>
          : <AnnualView year={year} data={annualData} onSelectMonth={selectMonth} />
      ) : tab === "monthly" ? (
        <MonthlyView year={year} month={month} onPrev={prevMonth} onNext={nextMonth} onYearChange={setYear} />
      ) : tab === "tableau" ? (
        <TableauPrevisionnelView year={year} />
      ) : (
        <PrevisionnelView year={year} />
      )}
    </div>
  );
}
